<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";

export default {
    name: "TOCTemp4",
    components: {
        SpellCheckedText
    },
    props: {
        toc: { type: Object, required: true },
        pageGap: { type: Boolean, default: true },
    },
    data() {
        return {
            pages: []
        };
    },
    computed: {
        words() {
            return ((this.toc && this.toc.title) || "Table of Contents").trim().split(" ");
        },
        firstLine() {
            return this.words.length > 1
                ? this.words.slice(0, -1).join(" ")
                : this.words[0] || "";
        },
        lastWord() {
            return this.words.length > 1
                ? this.words[this.words.length - 1]
                : "";
        }
    },
    watch: {
        toc: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    this.splitContentIntoPages();
                });
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.splitContentIntoPages();
        });
    },
    methods: {
        splitContentIntoPages() {
            if (!this.toc || !this.toc.pages) return;

            const itemsPerPage = parseInt(this.toc.itemsPerPage);
            const projects = this.toc.projects || [];
            let allTocItems = [...this.toc.pages].map((item, index) => ({
                ...item,
                alphabet: this.getAlphabet(index)
            }));

            this.pages = [];
            let pagesProjectsIndex = 0;

            while (allTocItems.length > 0) {
                let currentPageItems = allTocItems.splice(0, itemsPerPage);

                this.pages.push({
                    items: currentPageItems,
                    project: projects[pagesProjectsIndex] || null
                });
                pagesProjectsIndex++;

                if (this.pages.length > 50) break;
            }

            if (this.pages.length === 0) {
                this.pages.push({ items: [], project: projects[0] || null });
            }
        },
        getAlphabet(index) {
            let result = "";
            index += 1;
            while (index > 0) {
                let rem = (index - 1) % 26;
                result = String.fromCharCode(97 + rem) + result;
                index = Math.floor((index - 1) / 26);
            }
            return result.toUpperCase();
        },
        updateFirstLine(value) {
            this.toc.title = value + " " + this.lastWord;
        },
        updateLastWord(value) {
            this.toc.title = this.firstLine + " " + value;
        }
    }
};
</script>

<template>
    <div class="main-container" :style="{
        gap: pageGap ? '20px' : '0px',
        '--header-color': toc.style.colorScheme && toc.style.colorScheme.header,
        '--body-color': toc.style.colorScheme && toc.style.colorScheme.body,
        '--footer-color': toc.style.colorScheme && toc.style.colorScheme.footer,
        '--heading-color': toc.style.heading.color,
        '--heading-size': toc.style.heading.size + 'px',
        '--heading-weight': toc.style.heading.bold ? 'bold' : 'normal',
        '--heading-style': toc.style.heading.italic ? 'italic' : 'normal',
        '--heading-decoration': toc.style.heading.underline ? 'underline' : 'none',
        '--heading-font': toc.style.heading.font,

        '--label-color': toc.style.label.color,
        '--label-size': toc.style.label.size + 'px',
        '--label-weight': toc.style.label.bold ? 'bold' : 'normal',
        '--label-style': toc.style.label.italic ? 'italic' : 'normal',
        '--label-decoration': toc.style.label.underline ? 'underline' : 'none',
        '--label-font': toc.style.label.font,

        '--text-color': toc.style.text.color || '#000000',
        '--text-size': toc.style.text.size + 'px',
        '--text-weight': toc.style.text.bold ? 'bold' : 'normal',
        '--text-style': toc.style.text.italic ? 'italic' : 'normal',
        '--text-decoration': toc.style.text.underline ? 'underline' : 'none',
        '--text-font': toc.style.text.font,
    }">
        <div class="sub-container" v-for="(page, pageIndex) in pages" :key="pageIndex">
            <!-- Solid Background Layer -->

            <div class="content">
                <div class="header" />
                <div class="content-wrapper">
                    <header class="toc-header" v-if="pageIndex === 0">
                        <h1 class="titles firstLine">
                            <SpellCheckedText :value="firstLine || ''" @input="updateFirstLine" />
                        </h1>
                        <h1 class="titles lastWord">
                            <SpellCheckedText :value="lastWord || ''" @input="updateLastWord" />
                        </h1>
                    </header>

                    <div class="toc-list">
                        <div v-for="(item, idx) in page.items" :key="idx" class="toc-row">
                            <div class="section-label">
                                Section {{ item.alphabet }}
                                <SpellCheckedText :value="item.label || ''" @input="item.label = $event" />.
                            </div>
                            <div class="section-title">
                                <SpellCheckedText :value="item.heading || ''" @input="item.heading = $event" />
                            </div>
                            <div class="dot-line"></div>
                            <div class="page-number">{{ item.page || '' }}</div>
                        </div>
                    </div>
                </div>

                <footer class="toc-footer">
                    <div class="footer-line"></div>
                    <div class="project-info">
                        <SpellCheckedText :value="toc.opprName || ''" @input="toc.opprName = $event" />
                    </div>
                </footer>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    display: flex;
    flex-direction: column;
    width: 211mm;
    min-height: 296.83mm;
    position: relative;
    box-sizing: border-box;

    .sub-container {
        position: relative;
        height: 296.83mm;
        overflow: hidden;

        .header {
            min-height: 60px;
            background: var(--header-color);
        }

        .content {
            position: relative;
            z-index: 2;
            height: 100%;
            display: flex;
            flex-direction: column;
            box-sizing: border-box;
            pointer-events: none;

            .content-wrapper {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                height: 100%;
                padding: 0px 50px;
                background: var(--body-color);

                .toc-header {
                    text-align: center;
                    pointer-events: auto;
                    margin-top: 40px;

                    .titles {
                        color: var(--heading-color);
                        font-family: var(--heading-font);
                        font-weight: var(--heading-weight);
                        font-style: var(--heading-style);
                        text-decoration: var(--heading-decoration);
                        margin: 0;
                        text-transform: uppercase;
                        letter-spacing: 5px;
                    }

                    .firstLine {
                        font-size: var(--heading-size);
                    }

                    .lastWord {
                        font-size: calc(var(--heading-size) * 2);
                    }
                }

                .toc-list {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    margin: 0 auto;
                    min-height: 400px;
                    width: 100%;
                    padding: 40px 80px;
                    pointer-events: auto;

                    .toc-row {
                        display: flex;
                        align-items: flex-start;

                        .section-label {
                            font-size: var(--label-size);
                            font-family: var(--label-font);
                            font-weight: var(--label-weight);
                            font-style: var(--label-style);
                            text-decoration: var(--label-decoration);
                            color: var(--label-color);
                            min-width: 100px;
                        }

                        .section-title {
                            font-size: var(--text-size);
                            font-family: var(--text-font);
                            font-weight: var(--text-weight);
                            font-style: var(--text-style);
                            text-decoration: var(--text-decoration);
                            color: var(--text-color);
                            min-width: 0;
                        }

                        .dot-line {
                            flex: 1;
                            border-bottom: 1.5px dotted var(--text-color);
                            margin: 16px 4px 0 4px;
                        }

                        .page-number {
                            font-size: var(--text-size);
                            font-family: var(--text-font);
                            font-weight: var(--text-weight);
                            font-style: var(--text-style);
                            text-decoration: var(--text-decoration);
                            color: var(--text-color);
                            text-align: right;
                            white-space: nowrap;
                        }
                    }
                }
            }

            .toc-footer {
                pointer-events: auto;
                margin-top: auto;
                height: fit-content;
                background: var(--footer-color);
                padding: 50px 80px;

                .footer-line {
                    width: 100%;
                    height: 2.5px;
                    background: black;
                    margin-bottom: 10px;
                }

                .project-info {
                    font-size: 16px;
                    color: black;
                    font-weight: bold;
                }
            }
        }
    }
}
</style>
